import React, { useState } from 'react';
import {
  Box,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  List,
  ListItemButton,
  Chip,
  Typography,
  Button,
  CircularProgress
} from '@mui/material';
import { Search as SearchIcon } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';

const GlobalSearch = ({ open, onClose }) => {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const handleSearch = async () => {
    if (!query.trim()) return;
    
    setLoading(true);
    try {
      const response = await api.get(`/search?q=${encodeURIComponent(query)}&limit=10`);
      setResults(response.data);
    } catch (error) {
      console.error('Search failed:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  const handleResultClick = (type, id) => {
    const routes = {
      document: '/documents',
      task: '/tasks',
      audit: '/audits',
      capa: '/capa'
    };
    
    navigate(routes[type]);
    onClose();
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Search</DialogTitle>
      <DialogContent>
        <Box sx={{ mb: 2 }}>
          <TextField
            fullWidth
            placeholder="Search documents, tasks, audits, CAPAs..."
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyPress={handleKeyPress}
            InputProps={{
              startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />
            }}
          />
          <Box sx={{ mt: 1, display: 'flex', justifyContent: 'flex-end' }}>
            <Button onClick={handleSearch} variant="contained" disabled={loading}>
              {loading ? <CircularProgress size={20} /> : 'Search'}
            </Button>
          </Box>
        </Box>

        {results && (
          <Box>
            {results.total === 0 ? (
              <Typography color="text.secondary">No results found</Typography>
            ) : (
              <>
                {results.documents.length > 0 && (
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle2" sx={{ mb: 1 }}>Documents ({results.documents.length})</Typography>
                    <List>
                      {results.documents.map((doc) => (
                        <ListItemButton key={doc.id} onClick={() => handleResultClick('document', doc.id)}>
                          <Box sx={{ flex: 1 }}>
                            <Typography variant="body1">{doc.title}</Typography>
                            <Typography variant="caption" color="text.secondary">
                              {doc.description}
                            </Typography>
                          </Box>
                          <Chip label={doc.status} size="small" />
                        </ListItemButton>
                      ))}
                    </List>
                  </Box>
                )}

                {results.tasks.length > 0 && (
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle2" sx={{ mb: 1 }}>Tasks ({results.tasks.length})</Typography>
                    <List>
                      {results.tasks.map((task) => (
                        <ListItemButton key={task.id} onClick={() => handleResultClick('task', task.id)}>
                          <Box sx={{ flex: 1 }}>
                            <Typography variant="body1">{task.title}</Typography>
                            <Typography variant="caption" color="text.secondary">
                              {task.description}
                            </Typography>
                          </Box>
                          <Chip label={task.status} size="small" />
                        </ListItemButton>
                      ))}
                    </List>
                  </Box>
                )}

                {results.audits.length > 0 && (
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle2" sx={{ mb: 1 }}>Audits ({results.audits.length})</Typography>
                    <List>
                      {results.audits.map((audit) => (
                        <ListItemButton key={audit.id} onClick={() => handleResultClick('audit', audit.id)}>
                          <Box sx={{ flex: 1 }}>
                            <Typography variant="body1">{audit.title}</Typography>
                            <Typography variant="caption" color="text.secondary">
                              {audit.scope}
                            </Typography>
                          </Box>
                          <Chip label={audit.status} size="small" />
                        </ListItemButton>
                      ))}
                    </List>
                  </Box>
                )}

                {results.capas.length > 0 && (
                  <Box>
                    <Typography variant="subtitle2" sx={{ mb: 1 }}>CAPAs ({results.capas.length})</Typography>
                    <List>
                      {results.capas.map((capa) => (
                        <ListItemButton key={capa.id} onClick={() => handleResultClick('capa', capa.id)}>
                          <Box sx={{ flex: 1 }}>
                            <Typography variant="body1">{capa.title}</Typography>
                            <Typography variant="caption" color="text.secondary">
                              {capa.description}
                            </Typography>
                          </Box>
                          <Chip label={capa.status} size="small" />
                        </ListItemButton>
                      ))}
                    </List>
                  </Box>
                )}
              </>
            )}
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default GlobalSearch;

